import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useInRouterContext, useLocation } from 'react-router-dom';

import { publishPageTitle } from '@/shared/lib/analytics/page-title-registry';

const BASE_URL = 'https://xtrading.kr';
const SITE_NAME = 'XTrading';
// 사이트 공통 OG 이미지 (index.html에 고정). 페이지별 image prop이 이 값과 동일하면 중복 출력하지 않음.
const DEFAULT_OG_IMAGE = 'https://xtrading.kr/images/og-image.png';

export interface BreadcrumbItem {
  name: string;
  path: string;
}

export interface PageMetaProps {
  title: string;
  description: string;
  /** 페이지 경로 (예: "/market"). 없으면 canonical = BASE_URL(홈). */
  path?: string;
  keywords?: string;
  /**
   * 페이지별 OG/Twitter 이미지 URL (절대 URL). 기본값(사이트 공통 og-image.png)과
   * 동일하면 출력하지 않음 — index.html의 공통 og:image와 중복 방지.
   */
  image?: string;
  /**
   * true → <meta name="robots" content="noindex, follow" /> 추가.
   * 개인화 페이지·정책 페이지 등에 적용.
   */
  noIndex?: boolean;
  /**
   * 파빵부스러기 배열. 지정 시 BreadcrumbList JSON-LD를 출력.
   * 미지정 시 path 기반으로 Home > 현재 title 2단계 자동 생성.
   * 빈 배열([])을 넘기면 BreadcrumbList 완전 비활성.
   */
  breadcrumb?: BreadcrumbItem[];
  /**
   * 페이지별 추가 JSON-LD 구조화 데이터. 단일 객체 또는 배열.
   * Helmet의 <script type="application/ld+json">으로 출력.
   * HowTo / FAQPage / ItemList 등 페이지 타입별 데이터를 여기로 전달.
   */
  jsonLd?: object | object[];
}

/** BreadcrumbList JSON-LD 생성 */
// TRAD-3701 — «이 경로의 제목»을 페이지 뷰 트래커에 알린다(Helmet 의 제목 반영 시점과 무관하게).
const RouterTitlePublisher = ({ title }: { title: string }) => {
  const { pathname, search } = useLocation();
  useEffect(() => {
    publishPageTitle(pathname + search, title);
  }, [pathname, search, title]);
  return null;
};

const WindowTitlePublisher = ({ title }: { title: string }) => {
  useEffect(() => {
    publishPageTitle(window.location.pathname + window.location.search, title);
  }, [title]);
  return null;
};

const buildBreadcrumbJsonLd = (items: BreadcrumbItem[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    name: item.name,
    item: `${BASE_URL}${item.path}`,
  })),
});

export const PageMeta = ({ title, description, path, keywords, image, noIndex, breadcrumb, jsonLd }: PageMetaProps) => {
  const fullTitle = `${title} | ${SITE_NAME}`;
  const inRouter = useInRouterContext();
  const url = path ? `${BASE_URL}${path}` : BASE_URL;

  // 페이지별 이미지가 공통 이미지와 다른 경우에만 head에 추가 (중복 방지)
  const pageImage = image && image !== DEFAULT_OG_IMAGE ? image : undefined;

  // BreadcrumbList JSON-LD 결정
  // breadcrumb=[] → 비활성. breadcrumb 미지정 + path 있음 → 자동 2단계 생성.
  let breadcrumbJsonLd: object | undefined;
  if (breadcrumb !== undefined) {
    if (breadcrumb.length > 0) {
      breadcrumbJsonLd = buildBreadcrumbJsonLd(breadcrumb);
    }
    // 빈 배열이면 undefined 유지 (비활성)
  } else if (path && path !== '/') {
    // 자동 생성: Home > 현재 (path 있고 홈이 아닌 경우)
    breadcrumbJsonLd = buildBreadcrumbJsonLd([
      { name: 'Home', path: '/' },
      { name: title, path },
    ]);
  }

  // jsonLd 배열 정규화
  const jsonLdList: object[] = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <>
      {inRouter ? <RouterTitlePublisher title={fullTitle} /> : <WindowTitlePublisher title={fullTitle} />}
      <Helmet>
        <title>{fullTitle}</title>
        <meta name="description" content={description} />
        {keywords && <meta name="keywords" content={keywords} />}
        {/* robots 단일 소스 — index.html 정적 robots 제거됨(noindex 페이지와 충돌 방지) */}
        <meta name="robots" content={noIndex ? 'noindex, follow' : 'index, follow'} />

        <link rel="canonical" href={url} />

        {/* TRAD-2728 — hreflang 을 내지 않는다. 종전엔 같은 URL 에 ko/en/x-default 를 선언했는데, 검색 색인 스냅샷은
            KO 단일(TRAD-2407, prerender.mjs `locale: 'ko-KR'`)이라 «영어 대체본이 있다»는 선언이 사실이 아니었다.
            재검토 조건: 로케일별 URL(/en/*)을 만들어 영어판을 실제로 프리렌더하게 되면(영어권 유입 목표·AdSense 승인 후
            수익 구조 변화 — TRAD-2728 안 (a)) 그때 정직한 hreflang 을 다시 넣는다. 가드: __tests__/PageMeta.hreflang.test.tsx */}

        <meta property="og:title" content={fullTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="website" />
        {/* og:locale 은 index.html 공통 고정 → 중복 출력 금지(og:locale:alternate 는 TRAD-2728 에서 제거) */}
        {/* pageImage가 있을 때만 출력 (공통 og:image와 다른 경우). 같으면 index.html 태그 유지. */}
        {pageImage && <meta property="og:image" content={pageImage} />}

        {/* twitter:card / twitter:image 는 index.html 공통 고정 */}
        <meta name="twitter:title" content={fullTitle} />
        <meta name="twitter:description" content={description} />
        {pageImage && <meta name="twitter:image" content={pageImage} />}

        {/* BreadcrumbList JSON-LD */}
        {breadcrumbJsonLd && <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>}

        {/* 페이지별 추가 JSON-LD (HowTo / FAQPage 등) */}
        {jsonLdList.map((ld, idx) => (
          <script key={idx} type="application/ld+json">
            {JSON.stringify(ld)}
          </script>
        ))}
      </Helmet>
    </>
  );
};
